/**
 * 核心渲染與時間驅動器 (Render Loop / Ticker)
 * 負責驅動 requestAnimationFrame 並提供高精度的 delta 與 elapsed 時間
 */
export default class Time {
    constructor() {
        // 使用 performance.now() 獲取高精度初始時間戳 (毫秒)
        this.start = performance.now()
        this.current = this.start
        this.elapsed = 0

        // 預設單影格時長 (約 60 FPS 的 16.6ms)，避免第一幀出現 0 或 NaN
        this.delta = 16.666

        this._running = true
        this.onTick = null

        // 綁定 tick 參照，避免每影格產生新的閉包
        this._tickHandler = this.tick.bind(this)

        // 啟動 requestAnimationFrame 迴圈
        this._rafId = window.requestAnimationFrame(this._tickHandler)
    }

    /**
     * 每影格執行的時間更新迴圈
     * @param {DOMHighResTimeStamp} timestamp - rAF 原生傳入的高精度時間戳
     */
    tick(timestamp) {
        if (!this._running) {
            return
        }

        // 1. 計算真實 delta
        let rawDelta = timestamp - this.current

        // 2.【關鍵防護】限制 Delta 最大上限 (例如 100ms)
        // 避免使用者切換 Tab 離開再切回來時，巨大的 Delta 導致 3D 物理或動畫暴走爆飛
        this.delta = Math.min(rawDelta, 100)

        this.current = timestamp
        this.elapsed = timestamp - this.start

        // 3. 觸發訂閱者的每幀回調 (如 Producer/Director 的渲染與更新邏輯)
        this.onTick?.()

        // 4. 驅動下一影格
        this._rafId = window.requestAnimationFrame(this._tickHandler)
    }

    /**
     * 暫停/停止時間迴圈
     * 用於 HMR (熱重載) 或組件卸載，防止記憶體洩漏與多重迴圈疊加
     */
    stop() {
        this._running = false
        if (this._rafId) {
            window.cancelAnimationFrame(this._rafId)
            this._rafId = null
        }
    }

    /**
     * 重新啟動時間迴圈（若之前呼叫過 stop）
     */
    startLoop() {
        if (this._running) {
            return
        }
        this._running = true
        this.current = performance.now()
        this._rafId = window.requestAnimationFrame(this._tickHandler)
    }

    /** Teardown 別名，保持與專案整體命名一致 */
    destroy() {
        this.stop()
        this.onTick = null
    }
}
